"use client";

import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import { goLinkHref } from "@/lib/affiliate/go-link";
import type { AffiliatePlatform } from "@/lib/affiliate/links";
import { useAnalytics } from "@/lib/analytics/hooks";
import { trackMonetizationEvent } from "@/lib/analytics/track";
import { recordArmClick } from "@/lib/monetization/ad-context-client";
import { appendPbid } from "@/lib/monetization/pbid";
import { readVariantId } from "@/lib/monetization/variant-id";
import { useClickId } from "./monetized-unit-context";

// Re-exported so existing call sites (and `tests/unit/affiliate/append-pbid.test.ts`)
// keep importing `appendPbid` from here — the implementation itself lives in
// `@/lib/monetization/pbid` (dependency-free) so the server-side `/go/[offer]`
// route can share it without pulling this "use client" module's posthog-js
// graph into the SSR worker bundle.
export { appendPbid };

type Props = {
	// Payment-rail platforms plus course (Educative), referral (Contra),
	// commerce grid (Shopee, global-market deals) affiliates. Tracking treats
	// `platform` as a free string, so this union only guards the call sites.
	platform:
		| AffiliatePlatform
		| "educative"
		| "contra"
		| "fenzo"
		| "shopee"
		| "global_deal";
	href: string;
	/**
	 * Go-route slug. When set, the link points at `/<locale>/go/<goSlug>`
	 * instead of `href`, so no crawler or scanner ever reaches the partner's
	 * tracking URL from our HTML (see `src/lib/affiliate/go-link.ts`). The route
	 * writes the click row, so the client D1 write is skipped here.
	 */
	goSlug?: string;
	surface: string;
	jobId?: string | null;
	locale?: string;
	/**
	 * Extra props merged into the `affiliate_click` PostHog event — mirrors
	 * `AffiliateImpression`'s `extra`. Mostly PostHog-only, with one exception:
	 * `variant_id` is ALSO written to D1 (`ad_affiliate_events.variant_id`), so
	 * a copy test can be settled from the store this project trusts rather than
	 * from PostHog alone. Everything else here stays PostHog-only.
	 */
	extra?: Record<string, unknown>;
	/** Extra handler run after the standard tracking (e.g. a legacy event). */
	onClick?: () => void;
	className?: string;
	style?: CSSProperties;
	children: ReactNode;
};

/**
 * Renders an outbound affiliate link.
 *
 * Compliance:
 * - `rel="sponsored noopener noreferrer"` — sponsored is Google's policy
 *   requirement; noopener+noreferrer cover the cross-origin opener safety.
 * - `target="_blank"` so users don't lose context on lokerdollar.
 * - Fires a PostHog `affiliate_click` event for attribution.
 *
 * The visible CTA copy + disclosure live outside this component — see
 * `OfferCard`.
 */
export function AffiliateLink({
	platform,
	href,
	goSlug,
	surface,
	jobId = null,
	locale,
	extra,
	onClick,
	className,
	style,
	children,
}: Props) {
	const { trackAffiliateClick } = useAnalytics();
	// Shares the impression's id when wrapped by `AffiliateImpression`; mints its
	// own otherwise so a standalone link still attributes.
	const clickId = useClickId();

	const handleClick: MouseEventHandler<HTMLAnchorElement> = () => {
		trackAffiliateClick({
			platform,
			surface,
			locale,
			job_id: jobId,
			click_id: clickId,
			...extra,
		});
		// A go-link click is written to D1 by the route under this same click id;
		// a second write here would count it twice.
		if (!goSlug) {
			trackMonetizationEvent({
				eventKind: "affiliate_click",
				platform,
				surface,
				clickId,
				variantId: readVariantId(extra),
				locale,
			});
		}
		// Wave 4c per-visitor cooldown ledger (localStorage, try/catch).
		recordArmClick(platform);
		onClick?.();
	};

	return (
		<a
			href={
				goSlug
					? goLinkHref(goSlug, locale === "en" ? "en" : "id", {
							surface,
							cid: clickId,
						})
					: appendPbid(href, clickId)
			}
			target="_blank"
			rel="sponsored noopener noreferrer"
			onClick={handleClick}
			className={className}
			style={style}
		>
			{children}
		</a>
	);
}
